import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../App';
import { useTranslation } from '../i18n';
import { inboxAPI, analysisAPI } from '../api';
import Header from '../components/Header';
import ShareModal from '../components/ShareModal';
import PaidFeatureGate from '../components/PaidFeatureGate';
import { 
  Inbox as InboxIcon, Copy, Check, Clock, MapPin, DollarSign, User, Mail, Phone,
  Search, Filter, ChevronDown, ChevronUp, ExternalLink, FileText, CheckCircle2,
  XCircle, AlertTriangle, Home, Bed, Bath, Square, StickyNote, Loader2, Send, ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';

const Inbox = () => {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const { t, lang } = useTranslation();
  
  const [submissions, setSubmissions] = useState([]);
  const [counts, setCounts] = useState({ all: 0, pending: 0, analyzed: 0, accepted: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [username, setUsername] = useState('');
  const [inboxUrl, setInboxUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const [creatingUsername, setCreatingUsername] = useState(false);
  const [preferredUsername, setPreferredUsername] = useState('');
  const [editingHandle, setEditingHandle] = useState(false);
  
  // Filter state
  const [activeFilter, setActiveFilter] = useState('all');
  
  // Share modal state
  const [showShareModal, setShowShareModal] = useState(false);
  
  // Selected submission for detail view
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);
  const [internalNotes, setInternalNotes] = useState('');

  const loadInbox = useCallback(async (status = 'all') => {
    if (!user) {
      setLoading(false);
      return;
    }
    try {
      const userId = user?.user_id || 'demo_user';
      
      // Get or create inbox username
      const usernameResponse = await inboxAPI.createUsername({ user_id: userId });
      if (usernameResponse.data.username) {
        setUsername(usernameResponse.data.username);
        // `inbox_url` is the frontend link; the field the page used to read did not
        // exist in the response, so Copy copied an empty string and the share modal
        // showed a blank URL.
        setInboxUrl(usernameResponse.data.inbox_url || usernameResponse.data.submission_link || '');
      }
      
      // Get inbox submissions with status filter
      const inboxResponse = await inboxAPI.getInbox(userId, status);
      setSubmissions(inboxResponse.data.submissions || []);
      if (inboxResponse.data.counts) {
        setCounts(inboxResponse.data.counts);
      }
    } catch (error) {
      console.error('Failed to load inbox:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadInbox(activeFilter);
  }, [loadInbox, activeFilter]);

  // The link the owner shares. Falls back to the canonical frontend URL so the
  // clipboard never receives an empty string.
  const shareableUrl = inboxUrl || (username ? `https://www.chekste.com/submit/${username}` : '');
  const copyToClipboard = async () => {
    if (!shareableUrl) return;
    try {
      await navigator.clipboard.writeText(shareableUrl);
      setCopied(true);
      toast.success(lang === 'en' ? 'Link copied to clipboard!' : '¡Enlace copiado!');
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error(lang === 'en' ? 'Could not copy — select the link and copy it' : 'No se pudo copiar — selecciona el enlace y cópialo');
    }
  };

  // The handle is the last part of the link (chekste.com/submit/<handle>). Changing it
  // INVALIDATES the previous link — no alias — so the page says so before asking the
  // server, and the server answers 400/409 with a reason the owner can act on.
  const HANDLE_RE = /^[a-z0-9][a-z0-9_-]{2,29}$/;
  const createCustomUsername = async () => {
    const wanted = preferredUsername.trim().toLowerCase();
    if (!wanted) {
      toast.error(lang === 'en' ? 'Please enter a handle' : 'Escribe un nombre para el enlace');
      return;
    }
    if (!HANDLE_RE.test(wanted)) {
      toast.error(lang === 'en'
        ? '3–30 characters: lowercase letters, numbers, dash or underscore'
        : '3–30 caracteres: minúsculas, números, guion o guion bajo');
      return;
    }
    if (wanted === username) { setEditingHandle(false); return; }
    if (username && !window.confirm(lang === 'en'
      ? `Change your link to chekste.com/submit/${wanted}?\n\nThe current link (…/submit/${username}) will stop working immediately. Anyone who has it will need the new one.`
      : `¿Cambiar tu enlace a chekste.com/submit/${wanted}?\n\nEl enlace actual (…/submit/${username}) dejará de funcionar de inmediato. Quien lo tenga necesitará el nuevo.`)) {
      return;
    }

    setCreatingUsername(true);
    try {
      const response = await inboxAPI.createUsername({ preferred_username: wanted });
      if (response.data.username) {
        setUsername(response.data.username);
        setInboxUrl(response.data.inbox_url);
        toast.success(lang === 'en' ? 'Your link was updated' : 'Tu enlace fue actualizado');
        setPreferredUsername('');
        setEditingHandle(false);
      }
    } catch (error) {
      const code = error.response?.data?.detail;
      const msg = code === 'handle_taken'
        ? (lang === 'en' ? 'That handle is already taken' : 'Ese nombre ya está en uso')
        : code === 'handle_invalid'
          ? (lang === 'en' ? 'That handle is not allowed' : 'Ese nombre no está permitido')
          : (code || (lang === 'en' ? 'Could not update the link' : 'No se pudo actualizar el enlace'));
      toast.error(msg);
    } finally {
      setCreatingUsername(false);
    }
  };

  const getStatusBadge = (status) => {
    const configs = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: Clock },
      analyzed: { bg: 'bg-blue-100', text: 'text-blue-800', icon: FileText },
      accepted: { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle2 },
      rejected: { bg: 'bg-red-100', text: 'text-red-800', icon: XCircle }
    };
    const config = configs[status] || configs.pending;
    const Icon = config.icon;
    const labels = {
      pending: t('inbox_status_pending'),
      analyzed: t('inbox_status_analyzed'),
      accepted: t('inbox_status_accepted'),
      rejected: t('inbox_status_rejected')
    };
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        <Icon className="w-3.5 h-3.5" />
        {labels[status] || labels.pending}
      </span>
    );
  };

  // Handle analyzing a submission
  const handleAnalyze = async (submission) => {
    setAnalyzing(true);
    try {
      const userId = user?.user_id || 'demo_user';
      
      // Run analysis
      const response = await analysisAPI.analyze({
        input_type: submission.property_url ? 'url' : 'address',
        input_value: submission.property_url || submission.input_value,
        asking_price: submission.price,
        user_id: userId,
        language: lang
      });
      
      const analysisData = response.data;
      
      // Update submission status to analyzed
      await inboxAPI.updateStatus(submission.submission_id, {
        user_id: userId,
        status: 'analyzed',
        analysis_id: analysisData.analysis_id,
        analysis_data: analysisData
      });
      
      // Navigate to results with the analysis
      navigate('/results/new', { 
        state: { 
          analysis: analysisData,
          fromInbox: true,
          submissionId: submission.submission_id
        }
      });
      
    } catch (error) {
      console.error('Analysis failed:', error);
      toast.error(lang === 'en' ? 'Failed to analyze deal' : 'Error al analizar trato');
    } finally {
      setAnalyzing(false);
    }
  };

  // Handle updating status
  const handleStatusUpdate = async (submission, newStatus) => {
    try {
      const userId = user?.user_id || 'demo_user';
      await inboxAPI.updateStatus(submission.submission_id, {
        user_id: userId,
        status: newStatus
      });
      
      // Refresh submissions
      loadInbox(activeFilter);
      setSelectedSubmission(null);
      toast.success(lang === 'en' ? 'Status updated' : 'Estado actualizado');
    } catch (error) {
      toast.error(lang === 'en' ? 'Failed to update status' : 'Error al actualizar');
    }
  };

  // Handle saving internal notes
  const handleSaveNotes = async () => {
    if (!selectedSubmission) return;
    
    setSavingNotes(true);
    try {
      const userId = user?.user_id || 'demo_user';
      await inboxAPI.updateNotes(selectedSubmission.submission_id, {
        user_id: userId,
        internal_notes: internalNotes
      });
      
      // Update local state
      setSubmissions(prev => prev.map(s => 
        s.submission_id === selectedSubmission.submission_id 
          ? { ...s, internal_notes: internalNotes }
          : s
      ));
      
      toast.success(lang === 'en' ? 'Notes saved' : 'Notas guardadas');
    } catch (error) {
      toast.error(lang === 'en' ? 'Failed to save notes' : 'Error al guardar notas');
    } finally {
      setSavingNotes(false);
    }
  };

  // Open submission detail
  const openSubmission = (submission) => {
    setSelectedSubmission(submission);
    setInternalNotes(submission.internal_notes || '');
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <PaidFeatureGate
        feature="deal_inbox"
        featureDisplayName={lang === 'en' ? 'Deal Inbox' : 'Buzón de Tratos'}
        user={user}
        lang={lang}
        authIcon={InboxIcon}
        authTitle={lang === 'en' ? 'Sign In to Access Your Deal Inbox' : 'Inicia Sesión para Acceder a tu Buzón'}
        authDescription={lang === 'en' 
          ? 'Create a free account or sign in to receive and manage deal submissions from wholesalers and agents.'
          : 'Crea una cuenta gratuita o inicia sesión para recibir y gestionar envíos de tratos de mayoristas y agentes.'}
        redirectAfterAuth="/inbox"
      >
      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8 max-w-6xl">
        {/* Back Button */}
        <button 
          onClick={() => navigate(-1)} 
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4 transition-colors"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          {lang === 'en' ? 'Back' : 'Regresar'}
        </button>
        
        {/* Page Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-primary mb-1 sm:mb-2">
            {t('inbox_title')}
          </h1>
          <p className="text-base sm:text-lg text-muted-foreground">
            {t('inbox_subtitle')}
          </p>
        </div>

        {/* Inbox Link Card */}
        <div className="bg-gradient-to-br from-secondary to-blue-700 rounded-2xl shadow-lg p-6 sm:p-8 mb-6 sm:mb-8 text-white">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
            <div className="flex-1">
              <h2 className="text-lg sm:text-xl font-semibold mb-2">
                {t('inbox_link_title')}
              </h2>
              <p className="opacity-90 mb-4 text-sm sm:text-base">
                {t('inbox_link_subtitle')}
              </p>
              
              {username && !editingHandle ? (
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                  <div className="bg-white/20 backdrop-blur-sm rounded-lg px-3 sm:px-4 py-2 sm:py-3 flex items-center flex-1">
                    <code className="text-xs sm:text-sm font-mono break-all" data-testid="inbox-link">{shareableUrl}</code>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={copyToClipboard}
                      className="p-3 bg-white/20 hover:bg-white/30 rounded-lg transition-colors flex items-center justify-center gap-2"
                      data-testid="copy-link-btn"
                    >
                      {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                      <span className="sm:hidden">{copied ? (lang === 'en' ? 'Copied!' : '¡Copiado!') : (lang === 'en' ? 'Copy' : 'Copiar')}</span>
                    </button>
                    <button
                      onClick={() => setShowShareModal(true)}
                      className="px-4 py-3 bg-white text-secondary rounded-lg font-medium hover:bg-slate-100 transition-all flex items-center gap-2"
                      data-testid="send-inbox-link-btn"
                    >
                      <Send className="w-4 h-4" />
                      <span className="hidden sm:inline">{lang === 'en' ? 'Send Link' : 'Enviar'}</span>
                    </button>
                    <button
                      onClick={() => { setPreferredUsername(username); setEditingHandle(true); }}
                      className="px-4 py-3 bg-white/20 hover:bg-white/30 rounded-lg font-medium transition-colors text-sm"
                      data-testid="edit-handle-btn"
                    >
                      {lang === 'en' ? 'Customize' : 'Personalizar'}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-2" data-testid="handle-editor">
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                    <div className="flex-1 flex items-center rounded-lg bg-white/20 border border-white/30 focus-within:border-white">
                      <span className="pl-3 text-white/70 text-sm font-mono hidden sm:inline">chekste.com/submit/</span>
                      <input
                        type="text"
                        value={preferredUsername}
                        onChange={(e) => setPreferredUsername(e.target.value)}
                        placeholder={lang === 'en' ? 'your-name' : 'tu-nombre'}
                        className="flex-1 min-w-0 px-2 py-3 bg-transparent placeholder-white/60 text-white focus:outline-none text-sm font-mono"
                        data-testid="username-input"
                        maxLength={30}
                        autoComplete="off"
                      />
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={createCustomUsername}
                        disabled={creatingUsername}
                        className="px-4 py-3 bg-white text-secondary rounded-lg font-medium hover:bg-slate-100 disabled:opacity-50 transition-all text-sm"
                        data-testid="create-username-btn"
                      >
                        {creatingUsername ? '...' : (lang === 'en' ? 'Save' : 'Guardar')}
                      </button>
                      {username && (
                        <button
                          onClick={() => { setEditingHandle(false); setPreferredUsername(''); }}
                          className="px-4 py-3 bg-white/20 hover:bg-white/30 rounded-lg font-medium transition-colors text-sm"
                          data-testid="cancel-handle-btn"
                        >
                          {lang === 'en' ? 'Cancel' : 'Cancelar'}
                        </button>
                      )}
                    </div>
                  </div>
                  <p className="text-xs opacity-90" data-testid="handle-warning">
                    {lang === 'en'
                      ? 'Lowercase letters, numbers, dash or underscore (3–30). Changing it makes your current link stop working — partners who have it will need the new one. Partners see your name, not the handle.'
                      : 'Minúsculas, números, guion o guion bajo (3–30). Al cambiarlo, tu enlace actual deja de funcionar — quien lo tenga necesitará el nuevo. Tus socios ven tu nombre, no el handle.'}
                  </p>
                </div>
              )}
            </div>
            <InboxIcon className="w-12 h-12 sm:w-16 sm:h-16 opacity-20 hidden lg:block" />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Submissions List */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-sm border border-border">
              {/* Header with Filters */}
              <div className="p-4 sm:p-6 border-b border-border">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <h2 className="text-lg sm:text-xl font-semibold">
                    {t('inbox_received')}
                  </h2>
                  
                  {/* Filter Tabs */}
                  <div className="flex flex-wrap gap-2">
                    {['all', 'pending', 'analyzed', 'accepted', 'rejected'].map((filter) => (
                      <button
                        key={filter}
                        onClick={() => setActiveFilter(filter)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                          activeFilter === filter
                            ? 'bg-secondary text-white'
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                        data-testid={`filter-${filter}`}
                      >
                        {filter === 'all' ? t('inbox_all') : t(`inbox_status_${filter}`)}
                        <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-white/20 text-xs">
                          {counts[filter] || 0}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Submissions List */}
              {loading ? (
                <div className="text-center py-10 sm:py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-secondary mx-auto"></div>
                  <p className="mt-2 text-muted-foreground text-sm">{lang === 'en' ? 'Loading inbox...' : 'Cargando buzón...'}</p>
                </div>
              ) : submissions.length === 0 ? (
                <div className="text-center py-10 sm:py-12">
                  <InboxIcon className="w-12 h-12 sm:w-16 sm:h-16 text-slate-200 mx-auto mb-4" />
                  <h3 className="text-base sm:text-lg font-semibold text-slate-600 mb-2">
                    {t('inbox_no_submissions')}
                  </h3>
                  <p className="text-muted-foreground text-sm mb-4 px-4">
                    {t('inbox_share_prompt')}
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {submissions.map((submission, index) => (
                    <div
                      key={submission.submission_id || index}
                      onClick={() => openSubmission(submission)}
                      className={`p-4 sm:p-5 hover:bg-slate-50 cursor-pointer transition-colors ${
                        selectedSubmission?.submission_id === submission.submission_id ? 'bg-blue-50 border-l-4 border-secondary' : ''
                      }`}
                      data-testid={`submission-${index}`}
                    >
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-2">
                        <div className="flex items-start gap-3">
                          <MapPin className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                          <div>
                            <h3 className="font-semibold text-base">{submission.input_value}</h3>
                            {submission.property_url && (
                              <div className="flex items-center gap-1 text-xs text-secondary mt-1">
                                <ExternalLink className="w-3 h-3" />
                                {lang === 'en' ? 'From URL' : 'De URL'}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-3 sm:flex-shrink-0">
                          {getStatusBadge(submission.status)}
                        </div>
                      </div>
                      
                      <div className="flex flex-wrap gap-3 sm:gap-4 text-xs sm:text-sm ml-8">
                        {submission.price && (
                          <div className="flex items-center gap-1.5 text-slate-600">
                            <DollarSign className="w-4 h-4" />
                            <span className="font-medium">${submission.price.toLocaleString()}</span>
                          </div>
                        )}
                        {submission.beds && (
                          <div className="flex items-center gap-1.5 text-slate-500">
                            <Bed className="w-4 h-4" />
                            <span>{submission.beds}</span>
                          </div>
                        )}
                        {submission.baths && (
                          <div className="flex items-center gap-1.5 text-slate-500">
                            <Bath className="w-4 h-4" />
                            <span>{submission.baths}</span>
                          </div>
                        )}
                        {submission.sqft && (
                          <div className="flex items-center gap-1.5 text-slate-500">
                            <Square className="w-4 h-4" />
                            <span>{submission.sqft.toLocaleString()} sqft</span>
                          </div>
                        )}
                        <div className="flex items-center gap-1.5 text-slate-400">
                          <Clock className="w-4 h-4" />
                          {new Date(submission.submitted_at).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right: Selected Deal Detail */}
          <div className="lg:col-span-1">
            {selectedSubmission ? (
              <div className="bg-white rounded-xl shadow-sm border border-border p-5 sticky top-6">
                <h3 className="font-bold text-lg text-primary mb-4 flex items-center gap-2">
                  <Home className="w-5 h-5 text-secondary" />
                  {lang === 'en' ? 'Deal Details' : 'Detalles del Trato'}
                </h3>
                
                {/* Property Info */}
                <div className="mb-4">
                  <p className="font-semibold text-primary">{selectedSubmission.input_value}</p>
                  {selectedSubmission.price && (
                    <p className="text-lg font-bold text-success mt-1">${selectedSubmission.price.toLocaleString()}</p>
                  )}
                  
                  <div className="flex flex-wrap gap-3 mt-3 text-sm">
                    {selectedSubmission.beds && (
                      <span className="flex items-center gap-1 text-slate-600">
                        <Bed className="w-4 h-4" /> {selectedSubmission.beds} bed
                      </span>
                    )}
                    {selectedSubmission.baths && (
                      <span className="flex items-center gap-1 text-slate-600">
                        <Bath className="w-4 h-4" /> {selectedSubmission.baths} bath
                      </span>
                    )}
                    {selectedSubmission.sqft && (
                      <span className="flex items-center gap-1 text-slate-600">
                        <Square className="w-4 h-4" /> {selectedSubmission.sqft.toLocaleString()} sqft
                      </span>
                    )}
                  </div>
                </div>
                
                {/* Submitted By */}
                <div className="mb-4 p-3 bg-slate-50 rounded-lg">
                  <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">
                    {t('inbox_submitted_by')}
                  </h4>
                  <div className="space-y-1.5 text-sm">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-slate-400" />
                      <span>{selectedSubmission.submitted_by_name || 'Anonymous'}</span>
                    </div>
                    {selectedSubmission.submitted_by_email && (
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-slate-400" />
                        <span className="truncate">{selectedSubmission.submitted_by_email}</span>
                      </div>
                    )}
                    {selectedSubmission.submitted_by_phone && (
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-slate-400" />
                        <span>{selectedSubmission.submitted_by_phone}</span>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Sender Notes */}
                {selectedSubmission.notes && (
                  <div className="mb-4 p-3 bg-yellow-50 border border-yellow-100 rounded-lg">
                    <h4 className="text-xs font-semibold text-yellow-800 uppercase mb-1">
                      {lang === 'en' ? 'Sender Notes' : 'Notas del Remitente'}
                    </h4>
                    <p className="text-sm text-yellow-700">{selectedSubmission.notes}</p>
                  </div>
                )}
                
                {/* Internal Notes */}
                <div className="mb-4">
                  <label className="text-xs font-semibold text-muted-foreground uppercase mb-2 flex items-center gap-1">
                    <StickyNote className="w-3.5 h-3.5" />
                    {t('inbox_internal_notes')}
                  </label>
                  <textarea
                    value={internalNotes}
                    onChange={(e) => setInternalNotes(e.target.value)}
                    placeholder={t('inbox_notes_placeholder')}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm resize-none h-20 focus:outline-none focus:border-secondary"
                  />
                  <button
                    onClick={handleSaveNotes}
                    disabled={savingNotes}
                    className="mt-2 px-3 py-1.5 text-xs font-medium bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {savingNotes ? '...' : (lang === 'en' ? 'Save Notes' : 'Guardar Notas')}
                  </button>
                </div>
                
                {/* Status */}
                <div className="mb-4">
                  <p className="text-xs text-muted-foreground mb-2">{lang === 'en' ? 'Current Status' : 'Estado Actual'}</p>
                  {getStatusBadge(selectedSubmission.status)}
                </div>
                
                {/* Actions */}
                <div className="space-y-2">
                  {/* Analyze Button */}
                  <button
                    onClick={() => handleAnalyze(selectedSubmission)}
                    disabled={analyzing}
                    className="w-full py-3 bg-secondary text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2 transition-all"
                    data-testid="analyze-submission-btn"
                  >
                    {analyzing ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        {lang === 'en' ? 'Analyzing...' : 'Analizando...'}
                      </>
                    ) : (
                      <>
                        <Search className="w-5 h-5" />
                        {t('inbox_analyze')}
                      </>
                    )}
                  </button>
                  
                  {/* Accept / Reject Buttons */}
                  {selectedSubmission.status !== 'accepted' && selectedSubmission.status !== 'rejected' && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleStatusUpdate(selectedSubmission, 'accepted')}
                        className="flex-1 py-2 bg-green-100 text-green-700 rounded-lg font-medium hover:bg-green-200 flex items-center justify-center gap-1.5 text-sm transition-all"
                      >
                        <CheckCircle2 className="w-4 h-4" />
                        {t('inbox_mark_accepted')}
                      </button>
                      <button
                        onClick={() => handleStatusUpdate(selectedSubmission, 'rejected')}
                        className="flex-1 py-2 bg-red-100 text-red-700 rounded-lg font-medium hover:bg-red-200 flex items-center justify-center gap-1.5 text-sm transition-all"
                      >
                        <XCircle className="w-4 h-4" />
                        {t('inbox_mark_rejected')}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm border border-border p-8 text-center">
                <InboxIcon className="w-12 h-12 text-slate-200 mx-auto mb-3" />
                <p className="text-muted-foreground text-sm">
                  {lang === 'en' ? 'Select a submission to view details' : 'Selecciona un envío para ver detalles'}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Share Inbox Link Modal */}
      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        title={lang === 'en' ? 'Share Your Deal Inbox' : 'Compartir Tu Buzón de Tratos'}
        subtitle={lang === 'en' 
          ? 'Send your inbox link to partners so they can submit deals directly'
          : 'Envía tu enlace de buzón a socios para que envíen tratos directamente'}
        shareUrl={shareableUrl}
        lang={lang}
        onSendEmail={async (email, message) => {
          await inboxAPI.sendInboxLink({
            recipient_email: email,
            personal_message: message,
            language: lang
          });
        }}
      />
      </PaidFeatureGate>
    </div>
  );
};

export default Inbox;
